import { usePostHog } from "@posthog/react";
import { useCallback } from "react";

import { advisorContextKindOf } from "../lib/advisor-context";
import type { AdvisorDrawerContext } from "../lib/advisor-context";
import { captureAdvisorDrawerOpened } from "../lib/advisor-drawer-analytics";
import type { AdvisorDrawerSource } from "../lib/advisor-drawer-analytics";
import { useAdvisorDrawerStore } from "../stores/advisor";

/**
 * The ONE way every Ask Sill entry point opens the drawer (plan 435 M6,
 * task asd-entry-repoint). Pairs `useAdvisorDrawerStore.openDrawer` with the
 * `advisor_drawer_opened{source, context_kind}` analytics emission so no
 * call site has to remember to fire both — and so the event only fires on
 * an actual closed→open transition, never on a redundant click while the
 * drawer is already open.
 */
export function useOpenAdvisorDrawer(): (
  source: AdvisorDrawerSource,
  context?: AdvisorDrawerContext,
) => void {
  const posthog = usePostHog();
  const openDrawer = useAdvisorDrawerStore((s) => s.openDrawer);

  return useCallback(
    (source: AdvisorDrawerSource, context?: AdvisorDrawerContext) => {
      const wasOpen = useAdvisorDrawerStore.getState().drawerOpen;
      openDrawer(context);
      if (wasOpen) return;
      captureAdvisorDrawerOpened(
        (event, properties) => posthog?.capture?.(event, properties),
        source,
        advisorContextKindOf(
          context ?? useAdvisorDrawerStore.getState().activeContext,
        ),
      );
    },
    [openDrawer, posthog],
  );
}
